/**
 * Authentication Service
 * Handles all authentication-related API calls to the backend
 */

import axios from 'utils/axios';

const API_BASE = '/api/v1/auth';

/**
 * Login with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} recaptchaToken - Optional reCAPTCHA token
 * @param {string} deviceFingerprint - Optional device fingerprint for "Remember this device"
 * @param {boolean} rememberDevice - Whether to trust this device for future MFA bypass
 * @returns {Promise} Login response with tokens or MFA requirement
 */
export const login = async (email, password, recaptchaToken = null, deviceFingerprint = null, rememberDevice = false) => {
  const response = await axios.post(`${API_BASE}/login`, {
    email,
    password,
    recaptcha_token: recaptchaToken,
    device_fingerprint: deviceFingerprint,
    remember_device: rememberDevice
  });
  return response.data;
};

/**
 * Trigger MFA method (send OTP via email/SMS or initiate Duo push)
 * @param {string} mfaToken - Temporary MFA token from login
 * @param {string} method - MFA method: 'duo_push', 'duo_sms', 'twilio_sms', 'email_otp', 'totp'
 * @returns {Promise} Trigger response with status and message
 */
export const triggerMFA = async (mfaToken, method) => {
  const response = await axios.post(`${API_BASE}/mfa/trigger`, {
    mfa_token: mfaToken,
    method
  });
  return response.data;
};

/**
 * Verify MFA (check push status or verify OTP code)
 * @param {string} mfaToken - Temporary MFA token from login
 * @param {string} passcode - Optional OTP code for SMS/Email/TOTP verification
 * @param {string} deviceFingerprint - Optional device fingerprint for "Remember this device"
 * @param {boolean} rememberDevice - Whether to trust this device for future MFA bypass
 * @param {string} setDefaultMethod - Optional MFA method to set as default after verification
 * @returns {Promise} Verification response with tokens if successful
 */
export const verifyMFA = async (mfaToken, passcode = null, deviceFingerprint = null, rememberDevice = false, setDefaultMethod = null) => {
  const response = await axios.post(`${API_BASE}/mfa/verify`, {
    mfa_token: mfaToken,
    passcode,
    device_fingerprint: deviceFingerprint,
    remember_device: rememberDevice,
    set_default_method: setDefaultMethod
  });
  return response.data;
};

/**
 * Which second factors the signed-in user may set up, and which they have.
 *
 * Filtered by their organization's MFA policy, so this is the same set login
 * will offer. `ready` distinguishes a method that is allowed from one that is
 * usable today (email needs a verified address, SMS a verified number, TOTP and
 * Duo an enrolment).
 * @returns {Promise} { mfa_active, org_restricted, email, phone, methods: [...] }
 */
export const getMyMfaMethods = async () => {
  const response = await axios.get(`${API_BASE}/mfa/my-methods`);
  return response.data;
};

/**
 * Setup TOTP authenticator for the SIGNED-IN user.
 *
 * The account comes from the access token; the email argument is retained for
 * call-site compatibility and ignored by the server.
 * @returns {Promise} Setup response with QR code and secret
 */
export const setupTOTP = async (userEmail) => {
  const response = await axios.post(`${API_BASE}/mfa/totp/setup`, {
    user_email: userEmail
  });
  return response.data;
};

/**
 * Verify and activate TOTP setup.
 *
 * Only the code matters — the server enrols the secret it issued for this user,
 * so `secret` is accepted for compatibility and ignored.
 * @param {string} secret - Unused; kept for existing call sites
 * @param {string} otpCode - OTP code from authenticator app
 * @returns {Promise} Verification response
 */
export const verifyTOTPSetup = async (secret, otpCode) => {
  const response = await axios.post(`${API_BASE}/mfa/totp/verify-setup`, {
    secret,
    otp_code: otpCode
  });
  return response.data;
};

/**
 * Whether the signed-in user is enrolled in Duo, and on which channels.
 * @returns {Promise} { identifier, enrolled, ready: [codes], devices }
 */
export const getMyDuoStatus = async () => {
  const response = await axios.get(`${API_BASE}/mfa/duo/status`);
  return response.data;
};

/**
 * Start Duo enrolment for the signed-in user (from the profile screen).
 *
 * Duo answers in one of two shapes: a new Duo user gets `activation_barcode` +
 * `activation_code`, an existing one with no device gets `enroll_portal_url` with
 * `use_portal: true`. Callers must handle both.
 * @returns {Promise} { status, use_portal, activation_barcode, activation_code, activation_url, enroll_portal_url }
 */
export const enrollMeInDuo = async () => {
  const response = await axios.post(`${API_BASE}/mfa/duo/enroll-me`, {});
  return response.data;
};

/**
 * Remove one of the signed-in user's own enrolments (e.g. a lost phone).
 * The server refuses to remove the last usable factor while MFA is active.
 * @param {string} code - Method code, e.g. 'totp'
 */
export const removeMfaEnrollment = async (code) => {
  const response = await axios.delete(`${API_BASE}/mfa/enrollment/${code}`);
  return response.data;
};

/**
 * Request Duo enrollment SMS
 * @param {string} mfaToken - Temporary MFA token
 * @returns {Promise} Enrollment response
 */
export const enrollDuo = async (mfaToken) => {
  const response = await axios.post(`${API_BASE}/mfa/duo/enroll`, {
    mfa_token: mfaToken
  });
  return response.data;
};

/**
 * Initiate Duo device enrollment with QR code
 * @param {string} mfaToken - Temporary MFA token
 * @returns {Promise} Enrollment response with QR code URL and activation details
 */
export const enrollDuoDevice = async (mfaToken) => {
  const response = await axios.post(`${API_BASE}/mfa/duo/enroll-device`, {
    mfa_token: mfaToken
  });
  return response.data;
};

/**
 * Check Duo enrollment status (poll this after showing QR code)
 * @param {string} mfaToken - Temporary MFA token
 * @returns {Promise} Status response - 'waiting', 'success', 'invalid', or 'error'
 */
export const checkDuoEnrollStatus = async (mfaToken) => {
  const response = await axios.post(`${API_BASE}/mfa/duo/enroll-status`, {
    mfa_token: mfaToken
  });
  return response.data;
};

/**
 * Request password reset email
 * @param {string} email - User email
 * @param {string} recaptchaToken - Optional reCAPTCHA token
 * @returns {Promise} Response with success status and message
 */
export const forgotPassword = async (email, recaptchaToken = null) => {
  const response = await axios.post(`${API_BASE}/forgot-password`, {
    email,
    recaptcha_token: recaptchaToken
  });
  return response.data;
};

/**
 * Reset password using token from email
 * @param {string} token - Password reset token from email link
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Confirm new password
 * @returns {Promise} Response with success status and message
 */
export const resetPassword = async (token, newPassword, confirmPassword) => {
  const response = await axios.post(`${API_BASE}/reset-password`, {
    token,
    new_password: newPassword,
    confirm_password: confirmPassword
  });
  return response.data;
};

/**
 * Set default MFA method for user
 * @param {string} mfaToken - Temporary MFA token from login
 * @param {string} method - MFA method to set as default: 'email_otp', 'twilio_sms', 'duo_push', 'totp'
 * @returns {Promise} Response with success status and new default method
 */
export const setDefaultMFA = async (mfaToken, method) => {
  const response = await axios.post(`${API_BASE}/mfa/set-default`, {
    mfa_token: mfaToken,
    method
  });
  return response.data;
};

/**
 * Logout - invalidate session on backend
 * @returns {Promise} Logout response
 */
export const logout = async () => {
  try {
    const response = await axios.post(`${API_BASE}/logout`);
    return response.data;
  } catch (error) {
    // Don't throw on logout failure - always clear client state
    console.warn('Backend logout call failed:', error);
    return { status: 'success', message: 'Logged out locally' };
  }
};

/**
 * MFA method display names and icons
 */
export const MFA_METHODS = {
  duo_push: {
    name: 'Duo Mobile Push',
    description: 'Approve login on your Duo Mobile app',
    icon: 'MobileOutlined'
  },
  duo_phone: {
    name: 'Phone Call',
    description: 'Receive a phone call to verify',
    icon: 'PhoneOutlined'
  },
  duo_sms: {
    name: 'Duo SMS',
    description: 'Receive SMS code via Duo',
    icon: 'MessageOutlined'
  },
  twilio_sms: {
    name: 'SMS Code',
    description: 'Receive SMS code to your phone',
    icon: 'MessageOutlined'
  },
  email_otp: {
    name: 'Email Code',
    description: 'Receive code via email',
    icon: 'MailOutlined'
  },
  totp: {
    name: 'Authenticator App',
    description: 'Use Google Authenticator or similar app',
    icon: 'SafetyOutlined'
  }
};

export default {
  login,
  triggerMFA,
  verifyMFA,
  setupTOTP,
  verifyTOTPSetup,
  enrollDuo,
  enrollDuoDevice,
  checkDuoEnrollStatus,
  forgotPassword,
  resetPassword,
  setDefaultMFA,
  logout,
  MFA_METHODS
};
